import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@libraryadmin/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@libraryadmin/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import {
  getLibraryAdminSession,
  logoutLibraryAdminFromServer,
} from "@libraryadmin/services/auth";

interface HeaderProps {
  title?: string;
  subtitle?: string;
  mobileMenuButton?: ReactNode;
}

export function Header({ title, subtitle, mobileMenuButton }: HeaderProps) {
  const navigate = useNavigate();
  const session = getLibraryAdminSession();
  const fullName = [session?.user.firstName, session?.user.lastName]
    .filter(Boolean)
    .join(" ");
  const displayName = fullName || session?.user.email || "Library Admin";

  return (
    <header className="sticky top-0 z-30 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="flex h-14 items-center justify-between px-3 sm:h-16 sm:px-6">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-4">
          {mobileMenuButton}
          {title && (
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold tracking-tight text-foreground sm:text-xl md:text-2xl">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-0.5 hidden truncate text-xs text-muted-foreground sm:block md:text-sm">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 rounded-full px-2 hover:bg-muted md:px-3"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-sm font-semibold text-white shadow-sm">
                  LA
                </div>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium leading-none text-foreground">
                    {displayName}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Librarian</p>
                </div>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{displayName}</p>
                {session?.user.email && fullName && (
                  <p className="text-xs text-muted-foreground">
                    {session.user.email}
                  </p>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Profile Settings</DropdownMenuItem>
              <DropdownMenuItem>Help & Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive"
                onClick={async () => {
                  try {
                    await logoutLibraryAdminFromServer();
                  } finally {
                    navigate("/library-admin/login", { replace: true });
                  }
                }}
              >
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
